import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { useDesktopPresence } from '../../lib/desktopPresence';
import { Icon } from '../../components/Icon';
import { Badge, PageHead } from '../../components/ui';

interface Latest {
  available: boolean;
  version?: string;
  sizeBytes?: number;
  filename?: string;
  updatedAt?: string;
  platform?: string;
}

function humanSize(bytes: number): string {
  if (bytes >= 1_073_741_824) return `${(bytes / 1_073_741_824).toFixed(2)} غ.ب`;
  return `${Math.round(bytes / 1_048_576)} م.ب`;
}

/**
 * تطبيقُ سطح المكتب — تنزيلُه وسببُ كونه أساسَ التشغيل.
 *
 * المراقبةُ ومركزُ الاتصال يعملان من التطبيق مباشرة: يلتقط نافذتَه وحدَها
 * (لا سطحَ المكتب)، ويسجّل صوتَ المكالمة من النظام، ويطلب الهاتفَ المقترن
 * بلا نافذةٍ وسيطة — وكلُّ ذلك بموافقةٍ واحدةٍ تُحفظ على الجهاز. والمتصفّحُ
 * بديلٌ يعمل حين يُفتح النظامُ فيه، لكنّه يعيد سؤالَ المشاركة كلَّ مرّة.
 */
export function DesktopApp() {
  const latest = useQuery({
    queryKey: ['desktop', 'latest'],
    queryFn: () => api<Latest>('/public/desktop/latest'),
    staleTime: 5 * 60_000,
  });

  const { active, inApp, ready } = useDesktopPresence();
  const data = latest.data;

  const statusBadge = !ready ? (
    <Badge kind="neutral">جارٍ التحقّق…</Badge>
  ) : inApp ? (
    <Badge kind="success">
      <Icon name="check" size={13} /> تعمل من التطبيق
    </Badge>
  ) : active ? (
    <Badge kind="success">
      <Icon name="check" size={13} /> التطبيق نشط على هذا الجهاز
    </Badge>
  ) : (
    <Badge kind="info">تعمل من المتصفّح</Badge>
  );

  return (
    <div className="page">
      <PageHead
        title="تطبيق سطح المكتب"
        subtitle="ثبّته لتشغيل المراقبة ومركز الاتصال باحترافية — من نافذة النظام وحدها"
        actions={statusBadge}
      />

      {ready && active && !inApp && (
        <div className="alert alert-success mb-4">
          <Icon name="check" size={16} />
          <div>
            التطبيقُ مثبَّتٌ ويعمل على هذا الجهاز. تُلتقط المراقبةُ وتُسجَّل المكالماتُ من التطبيق
            — افتح النظامَ منه للحصول على أفضل جودة.
          </div>
        </div>
      )}

      <div className="two-col">
        <div className="card">
          <div className="card-head">
            <h2 className="card-title">{active ? 'إعادة التثبيت أو التحديث' : 'تنزيل التطبيق'}</h2>
            {data?.available && data.version && (
              <span className="tiny mute">النسخة {data.version}</span>
            )}
          </div>
          <div className="card-body">
            {latest.isPending && <div className="skeleton" style={{ height: 90 }} />}

            {data && !data.available && (
              <div className="alert alert-warning">
                لا توجد نسخةٌ منشورةٌ بعد. اطلب من فريق GoyOne رفع آخر نسخة.
              </div>
            )}

            {data?.available && (
              <>
                <p className="small mute">
                  ويندوز · {data.sizeBytes ? humanSize(data.sizeBytes) : ''}
                  {data.updatedAt ? ` · حُدّث في ${date(data.updatedAt)}` : ''}
                </p>
                <a
                  className={`btn mt-3${active ? '' : ' btn-primary'}`}
                  href="/api/public/desktop/installer"
                  download
                >
                  <Icon name="download" size={16} />
                  {active ? 'تنزيل آخر نسخة' : 'تنزيل المثبّت'}
                </a>
                <p className="tiny mute mt-3">
                  التثبيتُ بنقرةٍ واحدة لكلّ مستخدم — بلا صلاحية مدير — ويعمل التطبيق مع تشغيل
                  الجهاز. وبعد الموافقة مرّةً على مشاركة النظام لا يُسأل ثانية.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="card-title">لماذا التطبيق؟</h2>
          </div>
          <div className="card-body">
            <ul className="col" style={{ gap: 10, listStyle: 'none', margin: 0, padding: 0 }}>
              <li className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                <Icon name="video" size={16} />
                <span className="small">
                  <strong>المراقبة من نافذة النظام وحدها</strong> — لا سطحَ المكتب ولا تطبيقاتِك
                  الأخرى؛ اللقطاتُ والفيديو والمشاهدةُ الحيّة تُلتقط من التطبيق إن كان مفتوحًا.
                </span>
              </li>
              <li className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                <Icon name="phone" size={16} />
                <span className="small">
                  <strong>مكالماتٌ مباشرة</strong> — الاتصالُ من الهاتف المقترن من الضغطة، وتسجيلُ
                  صوت المكالمة، والكتمُ والإقفال من الشاشة.
                </span>
              </li>
              <li className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
                <Icon name="check" size={16} />
                <span className="small">
                  <strong>موافقةٌ واحدة</strong> تُحفظ على الجهاز، فلا تتكرّر نافذةُ المشاركة كلَّ
                  مرّةٍ كما في المتصفّح.
                </span>
              </li>
            </ul>

            <div className="alert alert-info mt-4">
              المتصفّحُ بديلٌ يعمل حين يُفتح النظامُ فيه: يلتقط تبويبَ النظام وحدَه ويسجّل، لكنّه
              يعيد سؤالَ المشاركة عند كلّ جلسة. للعمل المتّصل طوال اليوم، التطبيقُ أثبت.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
